'use client'

import type { Address } from 'viem'

import { InterfaceChainUtils } from '@morpho-org/dapp-core'
import { useMandatoryContext } from '@repo/hooks'
import { useQuery } from '@tanstack/react-query'
import { createContext, type PropsWithChildren, useEffect } from 'react'

import { useServiceUnavailable } from '@/contexts/ServiceUnavailable/ServiceUnavailable.context'
import { useWeb3Context } from '@/contexts/Web3Context'
import { orpc } from '@/lib/orpc/client'

export interface BundlerConfigurationContextProps {
  withSimplePermit?: Set<Address>
}

export const BundlerConfigurationContext = createContext<BundlerConfigurationContextProps | null>(
  null
)

export const useBundlerConfiguration = () => {
  return useMandatoryContext(BundlerConfigurationContext, 'BundlerConfigurationContext')
}

export type BundlerConfigurationContextProviderProps = PropsWithChildren

const BundlerConfigurationContextProvider = ({
  children
}: BundlerConfigurationContextProviderProps) => {
  const { targetChainId } = useWeb3Context()
  const { signalServiceUnavailable } = useServiceUnavailable()

  const { data: configuration, error: errorBundlerConfiguration } = useQuery(
    orpc.shared.bundlerConfiguration.queryOptions({ input: { chainId: targetChainId } })
  )

  useEffect(() => {
    if (errorBundlerConfiguration) {
      signalServiceUnavailable()
    }
  }, [errorBundlerConfiguration, signalServiceUnavailable])

  const value = {
    withSimplePermit: new Set<Address>([
      ...(InterfaceChainUtils.getAssetsWithSimplePermit(targetChainId) ?? []),
      ...(configuration?.withSimplePermit ?? [])
    ])
  }

  return (
    <BundlerConfigurationContext.Provider value={value}>
      {children}
    </BundlerConfigurationContext.Provider>
  )
}

export default BundlerConfigurationContextProvider
